import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import { AppDispatch } from '../../store/store';
import { registerUser } from '../../store/UserSlice';
import { registerSchema, RegisterFormData } from '../../schemas/auth.schema';
import Input from '../../components/form/Input';
import Button from '../../components/form/Button';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { ArrowLeft } from 'heroicons-react';

export default function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      fullName: "",
      email: "",
      password: "",
      confirmPassword: "",
      isAdmin: false,
    },
    mode: "onSubmit",
  });

  const onSubmit = async (data: RegisterFormData) => {
    const { confirmPassword, isAdmin, ...userPayloadWithoutConfirmPassword } = data;

    const userPayloadWithAdmin = {
      ...userPayloadWithoutConfirmPassword,
      isAdmin: isAdmin || false,
    };

    try {
      const result = await dispatch(registerUser(userPayloadWithAdmin));

      if (registerUser.fulfilled.match(result)) {
        const isSuccess = result.payload;

        if (isSuccess) {
          toast.success(t('register.success'));
          navigate('/login');
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || t('register.failed'));
    }
  };


  return (

    <div className="flex flex-col lg:flex-row min-h-screen lg:h-screen lg:overflow-hidden">
      <div className="hidden lg:flex w-1/2 h-full">
        <img src='./images/loginpic.jpg' className='object-cover w-full h-full' alt="sideimage" />
      </div>
      {/* Register Section */}
      <div className="w-full lg:w-1/2 px-6 sm:px-12 py-3 sm:py-4 flex flex-col justify-between h-full overflow-y-auto">
        {/* Top bar with Back button and LanguageSwitcher */}
        <div className="w-full flex items-center justify-between pb-1 shrink-0">
          <Link to="/" className="text-[#037897] font-gilroy-medium text-sm flex items-center hover:opacity-80 transition-opacity">
            <ArrowLeft className="w-4 inline-block mr-2" /> {t('register.backHome')}
          </Link>
          <LanguageSwitcher isMobile={true} />
        </div>

        {/* Main form container */}
        <div className="w-full max-w-2xl mx-auto my-auto py-2">
          <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col gap-4">
            {/* Header */}
            <div className='text-center'>
              <Link to="/" className="inline-block">
                <img
                  src='./images/bluelogo-new.png'
                  alt="Logo"
                  className='mx-auto mb-2 max-w-[120px] md:max-w-[140px]'
                />
              </Link>
              <h2 className='mb-1 font-gilroy text-[#263238] font-semibold text-[24px] md:text-[28px]'>
                {t('register.title')}
              </h2>
              <p className='text-gray-7 text-[#78909C] font-gilroy text-[15px] md:text-[17px]'>
                {t('register.subtitle')}
              </p>
            </div>

            {/* Full Name */}
            <Input
              id="fullName"
              label={t('register.fullNameLabel')}
              type="text"
              placeholder={t('register.fullNamePlaceholder')}
              autoComplete="name"
              error={errors.fullName?.message}
              {...register("fullName")}
            />

            {/* Email */}
            <Input
              id="email"
              label={t('register.emailLabel')}
              type="email"
              placeholder={t('register.emailPlaceholder')}
              autoComplete="email"
              error={errors.email?.message}
              {...register("email")}
            />

            {/* Password */}
            <div>
              <Input
                id="password"
                label={t('register.passwordLabel')}
                type="password"
                placeholder={t('register.passwordPlaceholder')}
                autoComplete="new-password"
                showPasswordToggle
                error={errors.password?.message}
                {...register("password")}
              />
              {!errors.password?.message && (
                <p className="text-[#78909C] text-xs mt-1">{t('register.passwordHint')}</p>
              )}
            </div>

            {/* Confirm Password */}
            <Input
              id="confirmPassword"
              label={t('register.confirmPasswordLabel')}
              type="password"
              placeholder={t('register.confirmPasswordPlaceholder')}
              autoComplete="new-password"
              showPasswordToggle
              error={errors.confirmPassword?.message}
              {...register("confirmPassword")}
            />

            {/* Register Button */}
            <Button
              isLoading={isSubmitting}
              loadingText={t('register.registering')}
              className="!mt-2"
            >
              {t('register.registerBtn')}
            </Button>

            {/* Login Link */}
            <h4 className='text-center text-sm sm:text-base'>
              {t('register.alreadyAccount')}{" "}
              <Link to='/login' className='text-[#037897] font-semibold'>
                {t('register.loginLink')}
              </Link>
            </h4>
          </form>
        </div>
      </div>
    </div>
  );
}
